import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import Button from '@components/Button';
import ModalHeader from '@components/Modal/ModalHeader';
import useDialogLabels from '@components/Modal/useDialogLabels';
import { ModalFooter } from '@components/Modal/ModalFooter';
import { PopupZIndexProvider, popupZIndexFor } from '@components/Modal/popupLayer';
import { MODAL_Z_INDEX } from '@constants/modal';

const StyledDrawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'aboveModal',
})(({ aboveModal }) => ({
    ...(aboveModal && { zIndex: MODAL_Z_INDEX.aboveModal }),
    '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: 356,
        overflow: 'hidden',
        backgroundColor: '#fff',
        height: '100%',
        boxShadow:
            '0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2)',
        '@media (max-width: 1024px)': {
            width: '100%',
        },
    },
}));

const Content = styled('div')({
    overflowY: 'auto',
    padding: '24px 24px 16px',
});

const Drawer = ({
    children,
    anchor,
    variant,
    className,
    open,
    onClose,
    title,
    subtitle = null,
    primaryText,
    primaryDisabled,
    primaryButtonType = 'contained',
    primaryButtonStartIcon = null,
    primaryAction,
    primaryActionComponent = null,
    secondaryText,
    secondaryAction = null,
    aboveModal = false,
}) => {
    const { headerProps, dialogProps } = useDialogLabels({ title, subtitle });
    const hasActions = primaryAction || primaryActionComponent || secondaryAction;
    return (
        <StyledDrawer
            variant={variant}
            anchor={anchor}
            open={open}
            onClose={onClose}
            className={className}
            aboveModal={aboveModal}
            slotProps={{ paper: { ...dialogProps, 'aria-modal': undefined } }}
        >
            <PopupZIndexProvider value={popupZIndexFor(aboveModal)}>
                <div style={{ height: '100%', display: 'grid', gridTemplateRows: 'max-content auto max-content' }}>
                    <ModalHeader
                        className="drawer-header"
                        padding={24}
                        titleVariant="h5"
                        title={title}
                        subtitle={subtitle}
                        {...headerProps}
                        onClose={onClose}
                    />
                    <Content className="drawer-content">{children}</Content>
                    {hasActions ? (
                        <ModalFooter className="drawer-footer" stacked padding={24}>
                            {primaryActionComponent ? (
                                primaryActionComponent
                            ) : (
                                <Button
                                    variant={primaryButtonType}
                                    disabled={primaryDisabled}
                                    onClick={primaryAction}
                                    startIcon={primaryButtonStartIcon}
                                >
                                    {primaryText}
                                </Button>
                            )}
                            {secondaryAction && (
                                <Button variant="outlined" onClick={secondaryAction}>
                                    {secondaryText}
                                </Button>
                            )}
                        </ModalFooter>
                    ) : null}
                </div>
            </PopupZIndexProvider>
        </StyledDrawer>
    );
};

export default Drawer;
